


import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import styled from 'styled-components';
import { COLORS } from '../Styles/Colors';
import { IoIosPin } from 'react-icons/io';
import { Company, CompanyFormInput, GpsDevice, GpsDeviceCompany, Location } from '../Types';
import useGetData from '../Hooks/useGetData';
import LocationForm from './Location/LocationForm';
import { AddButton, CancelButton, CloseButton, DeleteButton, DeleteConfirmButton, EditButton, ErrorMessage, GridContainer, LoadingMessage, Message, ModalContent, ModalHeader, ModalOverlay, PageContainer, RecordCount, TealSwitch, Title, TrackIconWrapper } from '../Styles/AppStyles';
import apiClient from '../Services/ApiClient';
import { decode } from '../Services/DecodeToken';
import { useSnackbar } from '../Context/SnackbarProvider';
import { PencilIcon } from 'lucide-react';
import GpsForm from './Gps/GpsForm';
import { Link } from 'react-router-dom';
import { BatteryBar } from '../Utils/batteryUtils';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;

interface AssignFormData {
  CID: string;
  deviceIds: string[];
}


const GpsPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setCompanyToDelete] = useState<Location | null>(null);

  const token: any = decode();

  const id = token.companyId;
  const roleLevel = token.roles.level;

  console.log(token);

  const { showSnackbar } = useSnackbar();

  const [data, loading, errorFetch, refetch] = useGetData<GpsDevice>(`/api/gps/devices`);
  const [companies] = useGetData<Company>('/api/company');
  const [companyDevices] = useGetData<GpsDeviceCompany>(`/api/gps/devices/company`);






  const filteredDevices = React.useMemo(() => {
  if (!data) return [];

  const seen = new Set<string>();
  const companyDeviceIds = new Set(companyDevices?.map(d => d.device_id));

  return data.filter(device => {
    const isCompanyDevice = roleLevel <= 50 ? companyDeviceIds.has(device.device_id) : true;
    const isUnique = !seen.has(device.device_id);

    if (isCompanyDevice && isUnique) {
      seen.add(device.device_id);
      return true;
    }

    return false;
  });
}, [roleLevel, companyDevices, data]);


  const handleSubmit = async (data: any) => {
    const response = await apiClient(`/api/gps/devices/company`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      showSnackbar("Failed to submit user", "error", "Something went wrong!");
      return;
    }

    setShowAddModal(false);
    showSnackbar(`Successfully added`, 'success', 'Successfully Added!');
    refetch();
  };

  const handleDeleteClick = (location: Location) => {
    setCompanyToDelete(location);
    setShowDeleteModal(true);
  };

   const getGpsDeviceColumnsNew = (roleLevel: number): ColumnDef[] => {
  const columns: ColumnDef[] = [];

  if (roleLevel >= 50) {
    columns.push({
      field: 'track',
      headerName: 'Track',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Link 
            to={`/maps?deviceId=${params.row.device_id}`} 
            title="View GPS Tracking"
            style={{ 
              display: 'flex', 
              width: '100%', 
              height: '100%', 
              justifyContent: 'center', 
              alignItems: 'center' 
            }}
          >
            <TrackIconWrapper>
              <TypedIoIosPin />
            </TrackIconWrapper>
          </Link>
        );
      },
    });
  }

  columns.push(
    { field: 'device_id', headerName: 'Device ID', width: 150, sortable: true, filterable: true, searchable: true },
    { field: 'name', headerName: 'Device Name', width: 180, sortable: true, filterable: true, searchable: true },
    { field: 'voltage', headerName: 'Battery', width: 150, sortable: true, renderCell: (params) => <BatteryBar voltage={params.row.voltage} /> },
    { field: 'gnss_run_status', headerName: 'GNSS Status', width: 130, type: 'number' },
    { field: 'fix_status', headerName: 'Fix Status', width: 120, type: 'number' },
    { field: 'utc_datetime', headerName: 'UTC Datetime', width: 180 },
    { field: 'latitude', headerName: 'Latitude', width: 130, type: 'number' },
    { field: 'longitude', headerName: 'Longitude', width: 130, type: 'number' },
    { field: 'altitude', headerName: 'Altitude (m)', width: 130, type: 'number' },
    { field: 'speed_over_ground', headerName: 'Speed (km/h)', width: 130, type: 'number' },
    { field: 'course_over_ground', headerName: 'Course', width: 130, type: 'number' },
    { field: 'fix_mode', headerName: 'Fix Mode', width: 110, type: 'number' },
    { field: 'hdop', headerName: 'HDOP', width: 100, type: 'number' },
    { field: 'pdop', headerName: 'PDOP', width: 100, type: 'number' },
    { field: 'vdop', headerName: 'VDOP', width: 100, type: 'number' },
    { field: 'satellites_in_view', headerName: 'Satellites', width: 110, type: 'number' },
    { field: 'cn0_max', headerName: 'CN₀ Max', width: 110, type: 'number' },
    { field: 'hpa', headerName: 'HPA', width: 100, type: 'number' },
    { field: 'recorded_at', headerName: 'Recorded At', width: 180 }
  );

  return columns;
};


  const getGpsDeviceColumns = (roleLevel: number): GridColDef[] => {
  const columns: GridColDef[] = [];

  if (roleLevel != 100) {
    columns.push({
      field: 'track',
      headerName: 'Track',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Link 
            to={`/maps?deviceId=${params.row.device_id}`} 
            title="View GPS Tracking"
            style={{ 
              display: 'flex', 
              width: '100%', 
              height: '100%', 
              justifyContent: 'center', 
              alignItems: 'center' 
            }}
          >
            <TrackIconWrapper>
              <TypedIoIosPin />
            </TrackIconWrapper>
          </Link>
        );
      },
    });
  }

  columns.push(
    { field: 'device_id', headerName: 'Device ID', width: 150, sortable: true, filterable: true },
    { field: 'name', headerName: 'Device Name', width: 180, sortable: true, filterable: true },
    { field: 'voltage', headerName: 'Battery', width: 150, sortable: true, renderCell: (params) => <BatteryBar voltage={params.row.voltage} /> },
    { field: 'gnss_run_status', headerName: 'GNSS Status', width: 130, type: 'number' },
    { field: 'fix_status', headerName: 'Fix Status', width: 120, type: 'number' },
    { field: 'utc_datetime', headerName: 'UTC Datetime', width: 180 },
    { field: 'latitude', headerName: 'Latitude', width: 130, type: 'number' },
    { field: 'longitude', headerName: 'Longitude', width: 130, type: 'number' },
    { field: 'altitude', headerName: 'Altitude (m)', width: 130, type: 'number' },
    { field: 'speed_over_ground', headerName: 'Speed (km/h)', width: 130, type: 'number' },
    { field: 'course_over_ground', headerName: 'Course', width: 130, type: 'number' },
    { field: 'fix_mode', headerName: 'Fix Mode', width: 110, type: 'number' },
    { field: 'hdop', headerName: 'HDOP', width: 100, type: 'number' },
    { field: 'pdop', headerName: 'PDOP', width: 100, type: 'number' },
    { field: 'vdop', headerName: 'VDOP', width: 100, type: 'number' },
    { field: 'satellites_in_view', headerName: 'Satellites', width: 110, type: 'number' },
    { field: 'cn0_max', headerName: 'CN₀ Max', width: 110, type: 'number' },
    { field: 'hpa', headerName: 'HPA', width: 100, type: 'number' },
    { field: 'recorded_at', headerName: 'Recorded At', width: 180 }
  );

  return columns;
};

  const gpsDeviceColumns = getGpsDeviceColumns(roleLevel);
  const gpsDeviceColumnsNew = getGpsDeviceColumnsNew(roleLevel);


  return (
    <PageContainer>

      <Header title='GPS Management' data={filteredDevices}>
        {roleLevel == 100 && (      <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
)}
      </Header>


      
      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            <ModalHeader>Assign Devices</ModalHeader>
            <GpsForm devices={filteredDevices} onSubmit={handleSubmit} onCancel={() => setShowAddModal(false)} />
          </ModalContent>
        </ModalOverlay>
      )}


      <DataTable
        data={filteredDevices}
        loading={loading}
        columns={gpsDeviceColumnsNew}
        pageKey="gps-devices"
        title="GPS Devices"
        initialPageSize={10}
        pageSizeOptions={[5, 10, 20]}
        idField="id"
      />

     
    </PageContainer>
  );
};



export default GpsPage;

